import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificationDatabaseService } from '../../services/notificationDatabaseService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const Notifications = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const notificationChannelRef = useRef(null);

  useEffect(() => {
    loadNotifications();
    setupRealtimeSubscription();

    return () => {
      if (notificationChannelRef.current) {
        notificationDatabaseService?.unsubscribeFromNotifications(notificationChannelRef.current);
      }
    };
  }, [userProfile]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationDatabaseService.getMyNotifications();
      setNotifications(data || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      notificationChannelRef.current = notificationDatabaseService.subscribeToNotifications(
        user.id,
        (newNotification) => {
          setNotifications((prev) => [newNotification, ...prev]);
        }
      );
    } catch (error) {
      console.error('Failed to setup notifications subscription:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationDatabaseService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true, isRead: true } : n))
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationDatabaseService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, isRead: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const isRead = (n) => n.is_read || n.isRead;

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'all') return true;
    if (filter === 'bookings') return notification.type === 'booking';
    if (filter === 'system') return notification.type !== 'booking';
    return true;
  });

  const unreadCount = notifications.filter((n) => !isRead(n)).length;

  const getNotificationIcon = (type) => {
    if (type === 'booking') return 'Calendar';
    if (type === 'payment') return 'DollarSign';
    if (type === 'review') return 'Star';
    return 'Bell';
  };

  const getNotificationIconStyle = (type) => {
    if (type === 'booking') return { bg: 'bg-primary/10', text: 'text-primary' };
    if (type === 'payment') return { bg: 'bg-green-100', text: 'text-green-600' };
    if (type === 'review') return { bg: 'bg-amber-100', text: 'text-amber-500' };
    return { bg: 'bg-slate-100', text: 'text-slate-600' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'الآن';
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    if (days < 7) return `منذ ${days} يوم`;
    return date.toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            الإشعارات
          </h1>
          <p className="text-sm text-slate-500">
            {unreadCount > 0 ? `${unreadCount} إشعار غير مقروء` : 'جميع الإشعارات مقروءة'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="px-4 py-2 bg-button text-button-foreground rounded-xl hover:opacity-90 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="CheckCheck" size={16} />
            <span>تحديد الكل كمقروء</span>
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-2">
        <div className="flex gap-2">
          {[
            { id: 'all', label: 'الكل', count: notifications.length },
            { id: 'bookings', label: 'الحجوزات', count: notifications.filter(n => n.type === 'booking').length },
            { id: 'system', label: 'النظام', count: notifications.filter(n => n.type !== 'booking').length },
          ].map((filterOption) => (
            <button
              key={filterOption.id}
              onClick={() => setFilter(filterOption.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                filter === filterOption.id
                  ? 'bg-button text-button-foreground shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {filterOption.label}
              {filterOption.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  filter === filterOption.id
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-100 text-slate-500'
                }`}>
                  {filterOption.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length > 0 ? (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => {
            const read = isRead(notification);
            const iconStyle = getNotificationIconStyle(notification.type);
            return (
              <div
                key={notification.id}
                className={`bg-white rounded-2xl shadow-sm border p-5 hover:shadow-md transition-all ${
                  !read ? 'border-primary/30 bg-primary/[0.02]' : 'border-slate-200'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`p-3 rounded-xl flex-shrink-0 ${iconStyle.bg}`}>
                    <Icon
                      name={getNotificationIcon(notification.type)}
                      size={22}
                      className={iconStyle.text}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className={`text-base font-bold leading-snug ${!read ? 'text-slate-900' : 'text-slate-800'}`}>
                        {notification.title || 'إشعار جديد'}
                      </h3>
                      {!read && (
                        <span className="w-2.5 h-2.5 bg-primary rounded-full flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mb-3 leading-relaxed">
                      {notification.message || notification.body || ''}
                    </p>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-400">
                        {formatDate(notification.created_at || notification.createdAt)}
                      </p>
                      {!read && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="text-xs text-primary font-semibold hover:underline transition-colors"
                        >
                          تحديد كمقروء
                        </button>
                      )}
                    </div>
                    {notification.type === 'booking' && (
                      <button
                        onClick={() => navigate('/customer-dashboard/bookings')}
                        className="mt-3 w-full py-2 px-4 border border-primary/30 text-primary rounded-xl text-sm font-medium hover:bg-primary/5 transition-colors"
                      >
                        عرض الحجز
                      </button>
                    )}
                    {notification.type === 'support' && (
                      <button
                        onClick={() => navigate('/customer-dashboard/help')}
                        className="mt-3 w-full py-2 px-4 border border-amber-300 text-amber-700 bg-amber-50 rounded-xl text-sm font-medium hover:bg-amber-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <Icon name="LifeBuoy" size={14} />
                        عرض التذكرة
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon name="BellOff" size={32} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">لا توجد إشعارات</h3>
          <p className="text-sm text-slate-500">
            {filter === 'all'
              ? 'لا توجد إشعارات حالياً'
              : `لا توجد إشعارات من نوع "${filter === 'bookings' ? 'الحجوزات' : 'النظام'}"`}
          </p>
        </div>
      )}
    </div>
  );
};

export default Notifications;
